import {useCallback, useState} from 'react';
import getStudyImagePath from '../../helper/firebase/storage/getStudyImagePath';
import uploadStorageFile from '../../helper/firebase/storage/uploadStorageFile';
import useImagePaste from '../../bonds/bondImageUpload/useImagePaste';

export interface UseImageUploadOptions {
  moduleId: string;
  topic: string;
  maxSizeBytes?: number;
  onUploadComplete?: (url: string) => void;
  onError?: (error: string) => void;
}

export interface UseImageUploadReturn {
  uploadImage: (file: File) => Promise<string | undefined>;
  handleFileSelect: (e: React.ChangeEvent<HTMLInputElement>) => Promise<void>;
  uploading: boolean;
  error: string | null;
}

/**
 * Hook to handle image uploads to Firebase Storage
 * Supports file selection via input and paste functionality
 */
export default function useImageUpload({
  moduleId,
  topic,
  maxSizeBytes = 5 * 1024 * 1024, // 5 MB default
  onUploadComplete,
  onError,
}: UseImageUploadOptions): UseImageUploadReturn {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const uploadImage = useCallback(
    async (file: File): Promise<string | undefined> => {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        const errorMsg = 'Please select an image file';
        setError(errorMsg);
        onError?.(errorMsg);
        return;
      }

      // Validate file size
      if (file.size > maxSizeBytes) {
        const errorMsg = `Image size must be less than ${Math.round(maxSizeBytes / 1024 / 1024)} MB`;
        setError(errorMsg);
        onError?.(errorMsg);
        return;
      }

      // Validate moduleId and topic
      if (!moduleId || !topic) {
        const errorMsg = 'Module ID and topic are required';
        setError(errorMsg);
        onError?.(errorMsg);
        return;
      }

      setError(null);
      setUploading(true);

      try {
        // Generate UUID filename with original extension
        const fileExtension = file.name.split('.').pop() || 'png';
        const filename = `${crypto.randomUUID()}.${fileExtension}`;
        const storagePath = getStudyImagePath(moduleId, topic, filename);

        // Upload file
        const downloadURL = await uploadStorageFile(file, storagePath);

        onUploadComplete?.(downloadURL);
        setUploading(false);
        return downloadURL;
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : 'Failed to upload image';
        setError(errorMsg);
        onError?.(errorMsg);
        setUploading(false);
        return;
      }
    },
    [moduleId, topic, maxSizeBytes, onUploadComplete, onError],
  );

  const handleFileSelect = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const {files} = e.target;
      if (!files || files.length === 0) return;

      await uploadImage(files[0]);
      // Reset input value to allow selecting the same file again
      e.target.value = '';
    },
    [uploadImage],
  );

  // Set up paste handler
  useImagePaste({
    onImagePaste: async (file) => {
      const url = await uploadImage(file);
      if (url) {
        onUploadComplete?.(url);
      }
    },
  });

  return {
    uploadImage,
    handleFileSelect,
    uploading,
    error,
  };
}
